import { ScaleLinear, ScaleTime } from 'd3-scale';
import { isUndefined } from 'lodash-es';

import { AnnotationsNumericalDisplay } from '../../../core/components/annotations/components/displays/AnnotationsNumericalDisplay.js';
import { AnnotationsTimeDisplay } from '../../../core/components/annotations/components/displays/AnnotationsTimeDisplay.js';
import { AnnotationsScrollWrapper } from '../../../core/components/annotations/components/overlays/AnnotationsScrollWrapper.js';
import { SCROLLBAR_THRESHOLD } from '../../../core/components/annotations/constants.js';
import { TextOverflowTruncateOptions } from '../../../core/components/annotations/types/marker.js';
import type { SelectionState } from '../../../core/components/annotations/types/state.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { ANNOTATIONS_PADDING_BOTTOM } from '../../constants/defaults.js';
import { useXYChartAnnotationsConfig } from '../../hooks/useXYChartAnnotationsConfig.js';
import { useXYChartAnnotationsData } from '../../hooks/useXYChartAnnotationsData.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useXYChartScales } from '../../hooks/useXYChartScales.js';
import { useSetState } from '../../store/selectors.js';
import { isNumericalAxis } from '../../types/guards.js';
import { AREAS } from '../../utils/grid-templates.js';

export const XYChartAnnotationsArea = () => {
  const scales = useXYChartScales();
  const annotationsConfig = useXYChartAnnotationsConfig();
  const data = useXYChartData();
  const annotationsData = useXYChartAnnotationsData();
  const setState = useSetState();
  const { width } = useGridPlotSize();

  const handleUpdateIndicators = (event: Partial<SelectionState>) => {
    setState((prev) => ({
      ...prev,
      annotations: {
        ...prev.annotations,
        ...event,
      },
    }));
  };

  if (isUndefined(annotationsConfig) || isUndefined(annotationsData)) {
    return null;
  }

  const { axes } = data;
  const { xAxisId, showLabels = false } = annotationsConfig;

  const axis = axes.find(({ id }) => id === xAxisId);
  const scale = scales.get(xAxisId);

  const trackNames = Array.from(annotationsData.keys());

  const serializedAnnotationsData = trackNames
    .map((name) => annotationsData.get(name))
    .filter((track) => !isUndefined(track));
  return (
    !isUndefined(axis) &&
    !isUndefined(scale) && (
      <ChartGrid.GridArea
        paddingBottom={ANNOTATIONS_PADDING_BOTTOM}
        gridArea={AREAS.annotations}
      >
        {({ availableWidth, availableHeight }) => {
          const labelsOffset = availableWidth - width;
          const annotationsCommonConfig = {
            tracks: serializedAnnotationsData,
            showLabels,
            labelsOffset,
            width: availableWidth,
            markerGroupHover: handleUpdateIndicators,
            textOverflowOptions: {
              textOverflow: 'truncate',
              truncateMode: 'middle',
            } as TextOverflowTruncateOptions,
            tooltipConfig: annotationsConfig.tooltip,
          };

          return (
            <AnnotationsScrollWrapper
              height={availableHeight}
              width={availableWidth + SCROLLBAR_THRESHOLD}
            >
              {isNumericalAxis(axis) ? (
                <AnnotationsNumericalDisplay
                  scale={scale.currentScale as ScaleLinear<number, number>}
                  {...annotationsCommonConfig}
                />
              ) : (
                <AnnotationsTimeDisplay
                  scale={scale.currentScale as ScaleTime<number, number>}
                  {...annotationsCommonConfig}
                />
              )}
            </AnnotationsScrollWrapper>
          );
        }}
      </ChartGrid.GridArea>
    )
  );
};
